// Phase 31 (plan 02): auth-gated media fetch primitives for the reusable chat surface.
// Reads per-instance config (authTokenKey, mediaProxyBase) from ChatConfigContext —
// no module-level mutable globals. Two simultaneous embeds on the same page
// (e.g. Bayon doc A and doc B in two tabs) cannot clobber each other because
// each <ChatConfigProvider> scopes its own values.

import { useEffect, useState } from 'react';
import { useChatConfig, type ChatConfig } from './ChatConfigContext';

// Default to the page's own origin so a stale `VITE_API_URL` baked into a
// cached bundle can't strand the miniapp on a dead host. When the env var is
// set (e.g. cross-origin --do mode), it wins; otherwise calls go same-origin
// against whatever URL the SuperApp / browser actually loaded the bundle from.
const API_URL = import.meta.env.VITE_API_URL || window.location.origin;

/**
 * Build the absolute proxy URL for a given mediaKey, given an explicit
 * mediaProxyBase. Pure function — safe to call outside a React component.
 */
export function buildMediaProxyUrl(mediaProxyBase: string, mediaKey: string): string {
  const base = mediaProxyBase.replace(/\/+$/, '');
  return `${API_URL}${base}/${mediaKey}`;
}

/**
 * Hook variant of buildMediaProxyUrl — reads mediaProxyBase from ChatConfigContext.
 * Must be called from a component / another hook.
 */
export function useMediaProxyUrl(mediaKey: string): string {
  const { mediaProxyBase } = useChatConfig();
  return buildMediaProxyUrl(mediaProxyBase, mediaKey);
}

export interface FetchSignedMediaUrlOptions {
  fileName?: string;
  attachment?: boolean;
}

/**
 * Ask the server for a short-lived signed URL that the native bridge can
 * open directly — bridge can't attach our JWT, so the auth-gated proxy URL
 * returns "Missing authentication" when passed to openExternalLink/shareFile.
 * The URL points to /api/media/dl/:token on our server (≤~200 bytes — keeps
 * native filesystem happy; bridge.shareFile on iOS derives the save-path
 * from the URL and hits ENAMETOOLONG if it's the ~500-byte R2 presigned URL).
 *
 * When `fileName` is passed we also ask the server to set Content-Disposition:
 * attachment, so the native saver writes the file with the original name.
 * For inline media (photo/video) pass { attachment: false } or omit fileName.
 *
 * Pure function variant — takes config explicitly. Safe for non-React callers.
 *
 * NOTE: the signed-URL endpoint lives at `${mediaProxyBase}/sign/:mediaKey` so
 * Bayon's `/api/bayon/media/sign/...` mirrors the technician's
 * `/api/media/sign/...` shape one-for-one.
 */
export async function fetchSignedMediaUrlWithConfig(
  config: ChatConfig,
  mediaKey: string,
  opts?: FetchSignedMediaUrlOptions,
): Promise<string | null> {
  const token = localStorage.getItem(config.authTokenKey);
  if (!token) return null;
  try {
    const params = new URLSearchParams();
    if (opts?.fileName) params.set('fileName', opts.fileName);
    if (opts?.attachment === false) params.set('attachment', 'false');
    const qs = params.toString() ? `?${params.toString()}` : '';

    const base = config.mediaProxyBase.replace(/\/+$/, '');
    const res = await fetch(`${API_URL}${base}/sign/${mediaKey}${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { url?: string };
    return data.url ?? null;
  } catch (err) {
    console.error('Failed to fetch signed media URL:', err);
    return null;
  }
}

/**
 * Hook variant — returns a callable that captures the current ChatConfigContext
 * value. Useful when a component needs to lazily fetch signed URLs in response
 * to user gestures (e.g. tap-to-open).
 */
export function useFetchSignedMediaUrl(): (
  mediaKey: string,
  opts?: FetchSignedMediaUrlOptions,
) => Promise<string | null> {
  const config = useChatConfig();
  return (mediaKey, opts) => fetchSignedMediaUrlWithConfig(config, mediaKey, opts);
}

/**
 * Fetch media via the auth-gated proxy, return a blob: URL for use in src
 * attributes. Returns null while loading. Revokes the blob URL on unmount or
 * when mediaKey / config changes.
 *
 * NOTE: useEffect's dep array includes config.authTokenKey and
 * config.mediaProxyBase so a context value change re-runs the fetch. The
 * technician miniapp never changes these mid-tree (defaults match today's
 * behavior), but the Bayon embed scopes them per mount so the deps preserve
 * correctness across simultaneous mounts.
 */
/** Pattern for our HMAC-tokened pre-signed download URLs. Matches both the
 *  technician shape (`/api/media/dl/<token>`) and the Bayon embed shape
 *  (`/api/bayon/media/dl/<token>`). NOT matched by raw R2 object URLs
 *  (`*.r2.dev/...` or `*.r2.cloudflarestorage.com/...`) — those have no token
 *  in them and must go through the auth-gated proxy.
 *
 *  Exported so non-hook callers (e.g. MediaContent.toCurrentOriginUrl) can
 *  reuse it instead of redefining a parallel check that drifts over time. */
export const PRE_SIGNED_URL_RE = /\/api\/(?:bayon\/)?media\/dl\//;

/** Convenience wrapper around PRE_SIGNED_URL_RE — handles null/empty inputs
 *  so callers don't have to. Returns true when `url` is one of our HMAC
 *  pre-signed download URLs (and thus should bypass host-rewriting / the
 *  auth-gated proxy). */
export function isPreSignedMediaUrl(url: string | null | undefined): boolean {
  return !!url && PRE_SIGNED_URL_RE.test(url);
}

export function useMediaBlobUrl(
  mediaKey: string | undefined,
  /**
   * Pre-signed URL from the API response (e.g. Bayon's `/api/media/dl/<HMAC-token>`).
   * When set AND it matches the pre-signed pattern, used directly as the
   * render src — no fetch, no auth header needed, because the HMAC token in
   * the URL is the auth. The proxy fetch is used for everything else,
   * including raw R2 object URLs (`*.r2.dev/...`) that earlier ticket-flow
   * code paths still pass through here as `fallbackUrl`.
   */
  signedUrl?: string | null,
): string | null {
  const config = useChatConfig();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    // Fast path: the message response already carries one of OUR pre-signed
    // URLs. Use it directly as `<img src>` — no fetch, no auth header. We
    // explicitly do NOT short-circuit on raw R2 object URLs (`*.r2.dev/...`)
    // because a) they bypass the auth-gated proxy and b) they're at the
    // mercy of Cloudflare's sandbox bucket toggle / r2.dev rate limit; when
    // either flips, every media bubble in the app silently breaks. Routing
    // those through the proxy keeps the surface durable.
    if (signedUrl && PRE_SIGNED_URL_RE.test(signedUrl)) {
      setBlobUrl(signedUrl);
      return;
    }

    if (!mediaKey) {
      setBlobUrl(null);
      return;
    }
    const token = localStorage.getItem(config.authTokenKey);
    if (!token) return;

    let objectUrl: string | null = null;
    let cancelled = false;

    fetch(buildMediaProxyUrl(config.mediaProxyBase, mediaKey), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => {
        if (cancelled || !blob) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setBlobUrl(null);
    };
  }, [mediaKey, signedUrl, config.authTokenKey, config.mediaProxyBase]);

  return blobUrl;
}
